import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AppVersionsDoc,
  DEFAULT_APP_VERSIONS,
  PlatformConfig,
  fetchAppVersions,
  isSemverLike,
  saveAppVersions,
} from '../services/appVersionService';

type PlatformKey =
  | 'client.android'
  | 'client.ios'
  | 'driver.android'
  | 'driver.ios';

type PlatformPath = {
  label: string;
  get: (doc: AppVersionsDoc) => PlatformConfig;
  set: (doc: AppVersionsDoc, cfg: PlatformConfig) => AppVersionsDoc;
};

const PATHS: Record<PlatformKey, PlatformPath> = {
  'client.android': {
    label: 'Client — Android',
    get: (doc) => doc.client.android,
    set: (doc, cfg) => ({ ...doc, client: { ...doc.client, android: cfg } }),
  },
  'client.ios': {
    label: 'Client — iOS',
    get: (doc) => doc.client.ios,
    set: (doc, cfg) => ({ ...doc, client: { ...doc.client, ios: cfg } }),
  },
  'driver.android': {
    label: 'Driver — Android',
    get: (doc) => doc.driver.android,
    set: (doc, cfg) => ({ ...doc, driver: { ...doc.driver, android: cfg } }),
  },
  'driver.ios': {
    label: 'Driver — iOS',
    get: (doc) => doc.driver.ios,
    set: (doc, cfg) => ({ ...doc, driver: { ...doc.driver, ios: cfg } }),
  },
};

// SemVer util simple (X.Y.Z)
function parseSemver(v: string): [number, number, number] | null {
  const m = (v || '').trim().match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!m) return null;
  return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)];
}
function cmpSemver(a: string, b: string): number {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  if (!pa || !pb) return 0; // invalide géré ailleurs
  for (let i = 0; i < 3; i++) {
    if (pa[i] > pb[i]) return 1;
    if (pa[i] < pb[i]) return -1;
  }
  return 0;
}

function PlatformEditor({
  title,
  value,
  onChange,
}: {
  title: string;
  value: PlatformConfig;
  onChange: (next: PlatformConfig) => void;
}) {
  const [local, setLocal] = useState<PlatformConfig>(value);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!isSemverLike(local.latest)) errs.push(`Version latest invalide (attendu X.Y.Z)`);
    if (!isSemverLike(local.min)) errs.push(`Version min invalide (attendu X.Y.Z)`);
    if (isSemverLike(local.latest) && isSemverLike(local.min)) {
      if (cmpSemver(local.min, local.latest) === 1) {
        errs.push(`min doit être ≤ latest`);
      }
    }
    return errs;
  }, [local]);

  const handleField =
    (key: keyof PlatformConfig) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = { ...local, [key]: e.target.value };
      setLocal(next);
      onChange(next);
    };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = { ...local, enforceMin: e.target.checked };
    setLocal(next);
    onChange(next);
  };

  const handleMsg =
    (lang: 'fr' | 'en') =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = { ...local, message: { ...(local.message || {}), [lang]: e.target.value } };
      setLocal(next);
      onChange(next);
    };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>

          {errors.length > 0 && (
            <Alert severity="warning">
              {errors.map((e, i) => (
                <div key={i}>• {e}</div>
              ))}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' },
            }}
          >
            <Box>
              <TextField
                fullWidth
                label="latest (X.Y.Z)"
                value={local.latest}
                onChange={handleField('latest')}
                error={!!local.latest && !isSemverLike(local.latest)}
                helperText={!local.latest || isSemverLike(local.latest) ? 'Version conseillée (soft update)' : 'Format X.Y.Z'}
              />
            </Box>

            <Box>
              <TextField
                fullWidth
                label="min (X.Y.Z)"
                value={local.min}
                onChange={handleField('min')}
                error={!!local.min && !isSemverLike(local.min)}
                helperText={!local.min || isSemverLike(local.min) ? 'Seuil minimal supporté' : 'Format X.Y.Z'}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={<Switch checked={!!local.enforceMin} onChange={handleToggle} />}
                label="Rendre la mise à jour obligatoire (min)"
              />
            </Box>

            <Box sx={{ gridColumn: { md: 'span 2' } }}>
              <TextField
                fullWidth
                label="Store URL"
                value={local.storeUrl}
                onChange={handleField('storeUrl')}
                placeholder="https://play.google.com/... ou https://apps.apple.com/..."
              />
            </Box>

            <Box sx={{ gridColumn: { md: 'span 2' } }}>
              <TextField
                fullWidth
                label="Message FR (optionnel)"
                value={local.message?.fr || ''}
                onChange={handleMsg('fr')}
              />
            </Box>
            <Box sx={{ gridColumn: { md: 'span 2' } }}>
              <TextField
                fullWidth
                label="Message EN (optionnel)"
                value={local.message?.en || ''}
                onChange={handleMsg('en')}
              />
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

const UpdateSettings: React.FC = () => {
  const [docState, setDocState] = useState<AppVersionsDoc>({ ...DEFAULT_APP_VERSIONS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchAppVersions();
        if (mounted) {
          setDocState(data);
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Erreur de chargement');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const setPlatform = (key: PlatformKey, cfg: PlatformConfig) => {
    const path = PATHS[key];
    setDocState((prev) => path.set(prev, cfg));
    setSuccess(false);
  };

  const validations = useMemo(() => {
    const errs: string[] = [];
    const check = (name: string, cfg: PlatformConfig) => {
      if (!isSemverLike(cfg.latest)) errs.push(`${name}: latest invalide (X.Y.Z)`);
      if (!isSemverLike(cfg.min)) errs.push(`${name}: min invalide (X.Y.Z)`);
      if (isSemverLike(cfg.latest) && isSemverLike(cfg.min)) {
        if (cmpSemver(cfg.min, cfg.latest) === 1) {
          errs.push(`${name}: min doit être ≤ latest`);
        }
      }
      if (!cfg.storeUrl) {
        // pas bloquant, mais utile
        // errs.push(`${name}: storeUrl est vide`);
      }
    };

    check('Client Android', docState.client.android);
    check('Client iOS', docState.client.ios);
    check('Driver Android', docState.driver.android);
    check('Driver iOS', docState.driver.ios);

    return errs;
  }, [docState]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      if (validations.length > 0) {
        setError(`Veuillez corriger les erreurs: \n- ${validations.join('\n- ')}`);
        return;
      }
      await saveAppVersions(docState);
      setSuccess(true);
    } catch (e: any) {
      setError(e?.message || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setDocState({ ...DEFAULT_APP_VERSIONS });
    setSuccess(false);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Mises à jour</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
       Configure les versions recommandées et minimales pour Client et Driver, par plateforme.
       Une version recommandée affiche un message de mise à jour. Une version minimale peut bloquer
       l’accès si la mise à jour devient obligatoire.
      </Typography>

      {loading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} /> <Typography>Chargement…</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {error && (
            <Alert severity="error">{error}</Alert>
          )}
          {success && !error && (
            <Alert severity="success">Paramètres enregistrés.</Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' },
            }}
          >
            <Box>
              <PlatformEditor
                title={PATHS['client.android'].label}
                value={PATHS['client.android'].get(docState)}
                onChange={(cfg) => setPlatform('client.android', cfg)}
              />
            </Box>
            <Box>
              <PlatformEditor
                title={PATHS['client.ios'].label}
                value={PATHS['client.ios'].get(docState)}
                onChange={(cfg) => setPlatform('client.ios', cfg)}
              />
            </Box>
          </Box>

          <Divider />

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' },
            }}
          >
            <Box>
              <PlatformEditor
                title={PATHS['driver.android'].label}
                value={PATHS['driver.android'].get(docState)}
                onChange={(cfg) => setPlatform('driver.android', cfg)}
              />
            </Box>
            <Box>
              <PlatformEditor
                title={PATHS['driver.ios'].label}
                value={PATHS['driver.ios'].get(docState)}
                onChange={(cfg) => setPlatform('driver.ios', cfg)}
              />
            </Box>
          </Box>

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button variant="outlined" onClick={handleResetDefaults} disabled={saving}>
              Réinitialiser (valeurs par défaut)
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default UpdateSettings;
